import React, { useCallback, useEffect, useState } from 'react';

import { FormFieldRenderer } from '@/components/forms/FormFieldRenderer';
import type { FieldGroup, FieldType } from '@/types/formSchema';
import { resolveHandoffFieldTargets } from '@/utils/handoffField';
import { readRelativePath } from '@/utils/humanCall/fieldStatus';

/**
 * Field types that have no meaningful blur (the control closes on selection), so their
 * value must be persisted on change instead. Free-text types commit on blur so we
 * don't fire a request per keystroke.
 */
const COMMIT_ON_CHANGE: ReadonlySet<FieldType> = new Set<FieldType>([
  'select',
  'multi_select',
  'radio',
  'checkbox',
  'date',
  'dob',
]);

interface HandoffFieldEditorProps {
  /** The handoff action's `field` — a short field name, or "case" for a case-level note. */
  fieldName: string | undefined;
  /** Output schema groups used to locate the field. */
  outputGroups: FieldGroup[];
  /** Current output data — supplies both the rendered values and the entity fan-out. */
  outputData: Record<string, unknown> | undefined;
  /** When false the field renders disabled (non-assignee / read-only audit). */
  canEdit: boolean;
  /** Persists one field. Resolves once the write lands. */
  onCommit: (path: string, value: unknown) => Promise<void> | void;
  /** Case id, for File-type fields. */
  caseRecordId?: string;
}

/** One resolved target: breadcrumb + input, with its own in-flight value. */
const TargetEditor: React.FC<{
  target: ReturnType<typeof resolveHandoffFieldTargets>[number];
  outputData: Record<string, unknown> | undefined;
  canEdit: boolean;
  onCommit: (path: string, value: unknown) => Promise<void> | void;
  caseRecordId?: string;
}> = ({ target, outputData, canEdit, onCommit, caseRecordId }) => {
  const persisted = readRelativePath(outputData, target.path);
  // Local draft so typing doesn't fire a request per keystroke. Re-seeded whenever the
  // persisted value changes underneath us (a save landing, or a poll refresh).
  const [draft, setDraft] = useState<unknown>(persisted);
  useEffect(() => setDraft(persisted), [persisted]);

  const commit = useCallback(
    (value: unknown) => {
      if (!canEdit) return;
      // Skip the write when nothing actually changed (blur fires on every tab-out).
      if (value === persisted) return;
      void onCommit(target.path, value);
    },
    [canEdit, persisted, onCommit, target.path],
  );

  const commitsOnChange = COMMIT_ON_CHANGE.has(target.field.type);
  const isNested = target.parents.length > 0;

  // Label stacked ABOVE the control, matching how the Output board renders a field in
  // edit mode (`EditableField`'s defaults: `text-xs text-muted-foreground mb-1` label,
  // `h-8` control, `px-2 py-2 -mx-2 rounded` row) so the two read as the same kind of
  // field. No hover tint — the field is always editable here, so there is nothing for a
  // hover affordance to reveal.
  const row = (
    <div className="-mx-2 rounded px-2 py-2">
      <div className="mb-1 break-words text-xs text-muted-foreground">
        {target.field.label}
        {target.field.validation?.required && <span className="ml-1 text-destructive">*</span>}
      </div>
      <FormFieldRenderer
        field={{ ...target.field, disabled: !canEdit }}
        value={draft}
        onChange={(value: unknown) => {
          setDraft(value);
          if (commitsOnChange) commit(value);
        }}
        onBlur={commitsOnChange ? undefined : () => commit(draft)}
        caseRecordId={caseRecordId}
        hideLabel
        controlHeightClass="h-8"
      />
    </div>
  );

  // A top-level field needs no context — render the control on its own. Only a nested
  // leaf gets the framed block carrying its parent chain, which is what makes an
  // otherwise-ambiguous label ("Copay") readable.
  if (!isNested) return row;

  return (
    <div className="rounded-md border border-muted bg-muted/20 px-2 py-1.5">
      <p className="truncate text-[11px] text-muted-foreground" title={target.parents.join(' › ')}>
        {target.parents.join(' › ')}
      </p>
      {row}
    </div>
  );
};

/**
 * Inline editor for the output field a handoff action points at (BI-1668).
 *
 * The AI auditor writes handoff actions as `{ field: "<short name>", note }`. Rather than
 * make the human auditor hunt that field down in the Output board, we resolve it against
 * the output schema and render it right under the note. Nested fields render with their
 * parent chain ("Benefits › Deductible") and entity fields with their instance
 * ("Drug · J1234"), because a bare leaf label is ambiguous on its own.
 *
 * A short name can legitimately resolve to more than one target (the same leaf under
 * several entity instances) — all are rendered, since the note applies to each. An
 * unresolvable or case-level `field` renders nothing, leaving just the note.
 *
 * HIPAA: field values are PHI — rendered and persisted only, never logged.
 */
export const HandoffFieldEditor: React.FC<HandoffFieldEditorProps> = ({
  fieldName,
  outputGroups,
  outputData,
  canEdit,
  onCommit,
  caseRecordId,
}) => {
  const targets = React.useMemo(
    () => resolveHandoffFieldTargets(outputGroups, outputData, fieldName),
    [outputGroups, outputData, fieldName],
  );

  if (targets.length === 0) return null;

  return (
    <div className="mt-1.5 space-y-1.5">
      {targets.map(target => (
        <TargetEditor
          key={target.path}
          target={target}
          outputData={outputData}
          canEdit={canEdit}
          onCommit={onCommit}
          caseRecordId={caseRecordId}
        />
      ))}
    </div>
  );
};
